/* eslint-disable no-restricted-syntax */
import React, { useEffect, useMemo, useState } from 'react'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import TextField from '@mui/material/TextField'
import { useTranslation } from 'react-i18next'
import Divider from '@mui/material/Divider'
import { Close } from '@mui/icons-material'
import Chip from '@mui/material/Chip'
import Box from '@mui/material/Box'

import { CategoriesType, CategoryType } from 'shared/store/useCategories/types'
import useFavouriteProducers, { useCopyFavouriteProducersMutation } from 'shared/store/useFavouriteProducers'
import useProducersSearch from 'shared/store/useProducersSearch'
import { useCategoriesQuery } from 'shared/store/useCategories'

import ContainedButton from 'shared/components/Buttons/ContainedButton'
import { ProducerSelect } from './components/producersSelect'
import { CategoriesTree } from './components/categoriesTree'
import CopyConfigModal from './components/copyConfigModal'
import UnsavedChangesModal from './components/unsavedChangesModal'

const availableShelfs = ['ECONOMY', 'MEDIUM_QUALITY', 'HIGH_QUALITY', 'PREMIUM']

interface Props {
   activeCategory: string
   setActiveCategory: (cat: string) => void
   locationUuid: string
   selectedProducers: {
      [key: string]: string[]
   }
   setSelectedProducers: React.Dispatch<
      React.SetStateAction<{
         [key: string]: string[]
      }>
   >
}

export default function FavouriteProducers({
   activeCategory,
   setActiveCategory,
   locationUuid,
   selectedProducers,
   setSelectedProducers,
}: Props) {
   const { t } = useTranslation(['TRANSLATION', 'LOCATIONS'])
   const [searchPhrase, setSearchPhrase] = useState('')

   const { data: categories } = useCategoriesQuery()
   const { data: shelfs } = useFavouriteProducers(locationUuid, activeCategory)
   const { mutate: copyConfig } = useCopyFavouriteProducersMutation()
   const [openCopyConfirmModal, setOpenCopyConfirmModal] = useState<boolean>(false)
   const [openUnsavedChangesModal, setOpenUnsavedChangesModal] = useState<{
      open: boolean
      uuid?: string
   }>({ open: false, uuid: undefined })

   function useDebounce<T>(value: T, delay: number): T {
      const [debouncedValue, setDebouncedValue] = useState(value)

      useEffect(() => {
         const handler = setTimeout(() => setDebouncedValue(value), delay)
         return () => clearTimeout(handler)
      }, [value, delay])

      return debouncedValue
   }

   const targetUuid = '01e539a4-e695-43ea-a358-29e2288db5c8'

   const extractSubtreeByUuid = (cats: CategoryType[], uuid: string): CategoryType[] => {
      for (const cat of cats) {
         if (cat.uuid === uuid) return [cat]
         if (cat.children?.length) {
            const childResult = extractSubtreeByUuid(cat.children, uuid)
            if (childResult.length) return [{ ...cat, children: childResult }]
         }
      }
      return []
   }

   const filteredCategoriesTree = useMemo(() => {
      if (!categories) return []

      const subtree = extractSubtreeByUuid(categories.categories, targetUuid)

      const sortRecursively = (cats: CategoryType[]): CategoryType[] =>
         cats
            ?.slice()
            ?.sort((a, b) => a.name.localeCompare(b.name, 'pl'))
            ?.map((cat) => ({
               ...cat,
               children: cat.children ? sortRecursively(cat.children) : [],
            }))

      return sortRecursively(subtree)
   }, [categories])

   const findCategoryNameByUuid = (categoriesTree: CategoryType[], uuid: string): string | undefined => {
      for (const category of categoriesTree) {
         if (category.uuid === uuid) {
            return category.name
         }
         if (category.children?.length) {
            const found = findCategoryNameByUuid(category.children, uuid)
            if (found) {
               return found
            }
         }
      }
      return undefined
   }

   const filterCategoriesByName = (
      cats: CategoryType[],
      term: string,
      expanded: Set<string> = new Set()
   ): { filtered: CategoryType[]; expanded: Set<string> } => {
      if (!term) return { filtered: cats, expanded }

      const result: CategoryType[] = []

      for (const cat of cats) {
         const match = cat.name.toLowerCase().includes(term.toLowerCase())
         if (match) {
            result.push(cat)
            expanded.add(cat.uuid)
         } else if (cat.children?.length) {
            const { filtered: filteredChildren, expanded: childExpanded } = filterCategoriesByName(
               cat.children,
               term,
               expanded
            )
            if (filteredChildren.length) {
               result.push({ ...cat, children: filteredChildren })
               expanded.add(cat.uuid)
            }
            childExpanded.forEach((id) => expanded.add(id))
         }
      }

      return { filtered: result, expanded }
   }
   const { filtered: filteredSet, expanded: expandedSet } = useMemo(() => {
      const { filtered, expanded } = filterCategoriesByName(filteredCategoriesTree, searchPhrase)

      if (targetUuid) {
         expanded.add(targetUuid)
      }

      return { filtered, expanded }
   }, [searchPhrase, filteredCategoriesTree])

   const filteredCategories: CategoriesType | undefined = filteredSet.length ? { categories: filteredSet } : undefined

   const [producersSearchPhrase, setProducersSearchPhrase] = useState('')
   const debouncedProducersSearch = useDebounce(producersSearchPhrase, 400)
   const { data: producers, isFetching } = useProducersSearch(debouncedProducersSearch)
   const { data: allProducers } = useProducersSearch('')

   const producerOptions = useMemo(() => producers?.results ?? [], [producers])

   function areShelfsEqual(a: Record<string, string[]>, b: Record<string, string[]>): boolean {
      const keysA = Object.keys(a)
      const keysB = Object.keys(b)

      if (keysA.length !== keysB.length) return false

      for (const key of keysA) {
         if (!b[key]) return false

         const arrA = [...a[key]].sort()
         const arrB = [...b[key]].sort()

         if (arrA.length !== arrB.length) return false

         // eslint-disable-next-line no-plusplus
         for (let i = 0; i < arrA.length; i++) {
            if (arrA[i] !== arrB[i]) return false
         }
      }

      return true
   }

   function handleCategoryChange(uuid: string) {
      if (shelfs) {
         const shelfsToCompare = {
            PREMIUM: Object.entries(shelfs.producers ?? {})
               .filter(([_, value]) => value === 'PREMIUM')
               .map(([key]) => key),
            HIGH_QUALITY: Object.entries(shelfs.producers ?? {})
               .filter(([_, value]) => value === 'HIGH_QUALITY')
               .map(([key]) => key),
            MEDIUM_QUALITY: Object.entries(shelfs.producers ?? {})
               .filter(([_, value]) => value === 'MEDIUM_QUALITY')
               .map(([key]) => key),
            ECONOMY: Object.entries(shelfs.producers ?? {})
               .filter(([_, value]) => value === 'ECONOMY')
               .map(([key]) => key),
         }
         if (!areShelfsEqual(shelfsToCompare, selectedProducers)) {
            setOpenUnsavedChangesModal({
               open: true,
               uuid,
            })
         } else {
            setActiveCategory(uuid)
         }
      }
   }

   useEffect(() => {
      setSelectedProducers({
         PREMIUM: [],
         HIGH_QUALITY: [],
         MEDIUM_QUALITY: [],
         ECONOMY: [],
      })
   }, [activeCategory])

   useEffect(() => {
      if (shelfs) {
         setSelectedProducers({
            PREMIUM: Object.entries(shelfs.producers ?? {})
               .filter(([_, value]) => value === 'PREMIUM')
               .map(([key]) => key),
            HIGH_QUALITY: Object.entries(shelfs.producers ?? {})
               .filter(([_, value]) => value === 'HIGH_QUALITY')
               .map(([key]) => key),
            MEDIUM_QUALITY: Object.entries(shelfs.producers ?? {})
               .filter(([_, value]) => value === 'MEDIUM_QUALITY')
               .map(([key]) => key),
            ECONOMY: Object.entries(shelfs.producers ?? {})
               .filter(([_, value]) => value === 'ECONOMY')
               .map(([key]) => key),
         })
      }
   }, [shelfs])

   return (
      <Box sx={{ m: '0 auto', py: 3, width: '100%' }}>
         <Box sx={{ display: 'flex', gap: '8px' }}>
            <Box flex={0.3}>
               <ContainedButton onClick={() => setOpenCopyConfirmModal(true)} fullWidth sx={{ mb: '8px' }}>
                  {t('LOCATIONS:favouriteProducers.copyConfig')}
               </ContainedButton>
               <TextField
                  fullWidth
                  placeholder={t('search')}
                  value={searchPhrase}
                  onChange={(e) => setSearchPhrase(e.target.value)}
                  slotProps={{
                     input: {
                        endAdornment: searchPhrase ? (
                           <IconButton onClick={() => setSearchPhrase('')}>
                              <Close />
                           </IconButton>
                        ) : null,
                     },
                  }}
               />
               {filteredCategories && (
                  <CategoriesTree
                     categories={filteredCategories}
                     selectedId={activeCategory}
                     expandedIds={expandedSet}
                     onSelect={(uuid) => handleCategoryChange(uuid)}
                  />
               )}
            </Box>

            {activeCategory ? (
               <Box flex={0.7} sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <Typography variant="h5" color="primary">
                     {t('LOCATIONS:favouriteProducers.selectedCategory', {
                        category: categories ? findCategoryNameByUuid(categories.categories, activeCategory) : '',
                     })}
                  </Typography>
                  {availableShelfs.map((item) => (
                     <Box key={item} sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <Divider flexItem>
                           <Typography variant="h6">{t(`TRANSLATION:${item.toLowerCase()}`).toUpperCase()}</Typography>
                        </Divider>
                        <ProducerSelect
                           producerOptions={producerOptions}
                           selectedProducers={selectedProducers[item]}
                           setSelectedProducers={(ids) =>
                              setSelectedProducers((prevState) => ({ ...prevState, [item]: ids }))
                           }
                           searchPhrase={producersSearchPhrase}
                           setSearchPhrase={setProducersSearchPhrase}
                           isFetching={isFetching}
                           t={t}
                           allSelectedProducers={Object.values(selectedProducers).flat()}
                        />
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                           {selectedProducers[item].map((uuid) => {
                              const producer = allProducers?.results.find((p) => p.uuid === uuid)
                              return (
                                 <Chip
                                    label={
                                       <Box sx={{ display: 'flex', color: 'white', gap: '4px', alignItems: 'center' }}>
                                          <Typography>{producer?.name || uuid}</Typography>
                                          <Close
                                             onClick={() => {
                                                setSelectedProducers((prevState) => ({
                                                   ...prevState,
                                                   [item]: [
                                                      ...prevState[item].filter(
                                                         (prevProducer) => uuid !== prevProducer
                                                      ),
                                                   ],
                                                }))
                                             }}
                                             sx={{
                                                '&:hover': {
                                                   color: 'red',
                                                   cursor: 'pointer',
                                                },
                                             }}
                                          />
                                       </Box>
                                    }
                                    color="primary"
                                    key={uuid}
                                 />
                              )
                           })}
                        </Box>
                     </Box>
                  ))}
               </Box>
            ) : (
               <Box
                  flex={0.7}
                  sx={{
                     display: 'flex',
                     flexDirection: 'column',
                     gap: '16px',
                     alignItems: 'center',
                     flexShrink: 0,
                  }}
               >
                  <Typography sx={{ pb: 2 }} textAlign="center" variant="h6" color="primary">
                     {t('LOCATIONS:favouriteProducers.chooseProducers')}
                  </Typography>
               </Box>
            )}
         </Box>
         <CopyConfigModal
            open={openCopyConfirmModal}
            onClose={() => setOpenCopyConfirmModal(false)}
            onSubmit={() =>
               copyConfig(
                  {
                     locationUuid,
                     categoryUuid: '',
                  },
                  {
                     onSuccess: () => {
                        setOpenCopyConfirmModal(false)
                     },
                  }
               )
            }
         />
         <UnsavedChangesModal
            open={openUnsavedChangesModal.open}
            onClose={() => setOpenUnsavedChangesModal({ open: false, uuid: undefined })}
            onSubmit={() => {
               setActiveCategory(openUnsavedChangesModal.uuid!)
               setOpenUnsavedChangesModal({ open: false, uuid: undefined })
            }}
         />
      </Box>
   )
}
